import { apiClient } from "../client";
import { User } from "@domain/Auth";
import { type IUser } from "@domain/Auth";
import { getLoginToken, NEW_USER_TOKEN, StandardAxiosConfig, REQUEST_CONFIG_NOAUTH, USER_NOT_FOUND } from "../common";
import { UserSettings } from "@domain/Settings";
import { env } from "../../config/env";
import type { IUsers, RegisterInput } from "@ports/auth/IUsers";

export interface ImpersonatableUser {
	id: string;
	email: string;
	name: string;
	companyIds: string[];
}

/**
 * Browser implementation of the `IUsers` port from `@ports/auth/IUsers`.
 * Has a wider surface than the port (impersonation, password change, profile
 * updates, etc.) — those legacy methods are still available on `API.Users` for
 * any code that hasn't been routed through a use case yet, but the auth use
 * cases see only the `IUsers` view.
 */
export class UsersAPI implements IUsers {
	baseUrl: string;

	constructor() {
		this.baseUrl = `${env.rogoApiUrl}/auth`;
	}

	public async requestPasswordReset(email: string): Promise<void> {
		await apiClient.post(`${this.baseUrl}/reset-password`, { email }, REQUEST_CONFIG_NOAUTH);
	}

	public async validateResetToken(token: string): Promise<string> {
		const response = await apiClient.post(`${this.baseUrl}/validate-token`, { token: token }, REQUEST_CONFIG_NOAUTH);
		return response.data.email;
	}

	/**
	 * Change password for the authenticated user. Verifies the current password
	 * before applying the new one. Distinct from `setNewPassword()`, which is the
	 * magic-link reset flow used by unauthenticated users.
	 */
	public async changePassword(currentPassword: string, newPassword: string) {
		const response = await apiClient.put(
			`${this.baseUrl}/password`,
			{ currentPassword, newPassword },
			StandardAxiosConfig(),
		);
		return response.data as { status: string };
	}

	public async setNewPassword(email: string = "", password: string = "", token: string = ""): Promise<boolean> {
		const params = new URLSearchParams();
		params.append("grant_type", "password");
		params.append("username", email);
		params.append("password", password);
		params.append("scope", "");
		params.append("client_id", "");
		params.append("client_secret", token);

		const response = await apiClient.post<{ access_token: string; token_type: string }>(
			`${this.baseUrl}/update-password`,
			params,
		);
		return response.status === 200;
	}

	public async register(input: RegisterInput): Promise<User | null> {
		const params = {
			username: input.username,
			password: input.password,
			firstName: input.firstName,
			lastName: input.lastName,
		};
		const response = await apiClient.post<{ access_token: string; token_type: string }>(
			`${this.baseUrl}/register`,
			params,
		);

		const token = response.data.access_token;
		if (token == NEW_USER_TOKEN) {
			// Pending-activation user has only the placeholder token and no profile yet.
			return { token } as unknown as User;
		}

		const userData = await this._meWithToken(token);
		if (!userData?.username) {
			throw new Error(USER_NOT_FOUND);
		}
		this._storeProgagricaTokens(userData);
		return User.fromIUser(userData, token);
	}

	/**
	 * POST /login + GET /me. Returns the User decorated with the access token.
	 * **Pure HTTP — no auth-token side effects.** The caller (the auth use case)
	 * is responsible for persisting the token via its `ITokenStore` port. This
	 * keeps the adapter platform-agnostic — a Node CLI can use the same code
	 * path without dragging in browser localStorage.
	 *
	 * Note: this method still writes Proagrica tokens to `localStorage` as part
	 * of the existing Proagrica integration. Promoting that to a port is a
	 * separate follow-up.
	 */
	public async login(email: string, password: string) {
		const params = new URLSearchParams();
		params.append("grant_type", "password");
		params.append("username", email || "");
		params.append("password", password || "");
		params.append("scope", "");
		params.append("client_id", "");
		params.append("client_secret", "");

		const response = await apiClient.post<{ access_token: string; token_type: string }>(
			`${this.baseUrl}/login`,
			params,
		);
		const token = response.data.access_token;

		const userData = await this._meWithToken(token);
		if (!userData?.username) {
			throw new Error(USER_NOT_FOUND);
		}
		this._storeProgagricaTokens(userData);
		return User.fromIUser(userData, token);
	}

	/**
	 * GET /me using an **explicitly supplied** bearer token rather than a token
	 * read from storage. Used during login, before the token has been persisted.
	 */
	private async _meWithToken(token: string) {
		const queryParams: string[] = [];
		queryParams.push(`use_cache=false`);
		queryParams.push(`environment_name=${env.environmentName}`);
		queryParams.push(`app_version=${env.appVersion}`);
		const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
		const url = `${this.baseUrl}/me${queryString}`;

		const response = await apiClient.get<IUser>(url, {
			headers: {
				Accept: "application/json",
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
		});
		return response.data;
	}

	public async me() {
		const queryParams: string[] = [];
		queryParams.push(`use_cache=false`);
		queryParams.push(`environment_name=${env.environmentName}`);
		queryParams.push(`app_version=${env.appVersion}`);
		const queryString = queryParams.length ? `?${queryParams.join("&")}` : "";
		const url = `${this.baseUrl}/me${queryString}`;

		const response = await apiClient.get<IUser>(url, StandardAxiosConfig());
		const data = response.data;

		this._storeProgagricaTokens(data);

		return User.fromIUser(data, getLoginToken()!);
	}

	private _storeProgagricaTokens(data: IUser) {
		// TODO if we are going to store these here we should also use expiring cookies
		localStorage.setItem("proagrica_access_token", data.proagricaAuth?.access_token || "");
		localStorage.setItem("proagrica_refresh_token", data.proagricaAuth?.refresh_token || "");
		// localStorage.setItem('proagrica_auth', JSON.stringify(user.proagrica_auth));
	}

	public async getImpersonatableUsers(): Promise<ImpersonatableUser[]> {
		const url = `${this.baseUrl}/impersonate/users`;
		const response = await apiClient.get<ImpersonatableUser[]>(url, StandardAxiosConfig());
		return response.data;
	}

	public async updatePortalSettings(email: string, value: UserSettings) {
		const url = `${this.baseUrl}/update/portal-settings?email=${email}`;
		await apiClient.post(url, value, StandardAxiosConfig());
	}
}
